import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Tooltip, 
  message, 
  Input, 
  Skeleton, 
  Avatar 
} from 'antd';
import { 
  EditOutlined, 
  SearchOutlined, 
  FileExcelOutlined, 
  FilePdfOutlined 
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getLicenses } from '../../services/licenseService';
import customerService from '../../services/customerService';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Search } = Input;

const CustomerCell = ({ customerLicenses }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!customerLicenses || customerLicenses.length === 0) {
        return;
      }

      const primaryCustomer = customerLicenses.find(cl => cl.is_primary) || customerLicenses[0];
      const customerId = primaryCustomer?.customer_id;

      if (!customerId) {
        setError('No customer ID found');
        return;
      }

      if (primaryCustomer.company_name) {
        setCustomer(primaryCustomer.company_name);
        return;
      }

      setLoading(true);
      try {
        const customerData = await customerService.getCustomer(customerId);
        setCustomer(customerData);
      } catch (err) {
        console.error('Error fetching customer:', err);
        setError('Failed to load customer data');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerLicenses]);

  if (loading) return <Skeleton.Input style={{ width: 150 }} active size="small" />;
  if (error) return <span>Error loading customer</span>;
  if (!customerLicenses || customerLicenses.length === 0) {
    return <span>Unassigned</span>;
  }

  const primaryCustomer = customerLicenses.find(cl => cl.is_primary) || customerLicenses[0];
  
  if (customer) {
    const displayName = customer.company_name || customer.contact_name || 'Unnamed Customer';
    const logoUrl = customer.company_logo ? (
      customer.company_logo.startsWith('uploads/') ? 
        `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}/static/${customer.company_logo.replace('uploads/', '')}` :
        customer.company_logo.startsWith('/static/') ?
          `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}${customer.company_logo}` :
          customer.company_logo
    ) : null;
    
    return (
      <Tooltip title={`ID: ${customer.id}${primaryCustomer.is_primary ? ' (Primary)' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {logoUrl && (
            <Avatar 
                src={logoUrl}
                size="small"
                style={{ 
                  backgroundColor: customer.company_logo ? 'transparent' : '#1890ff',
                  border: customer.status === 'active' ? '1px solid #19f705' : '1px solid #fc0202',
                }}
            />
          )}
          <span>{displayName}</span>
        </div>
      </Tooltip>
    );
  }

  return <span>Loading customer...</span>;
};

const InactiveLicensesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: 'INACTIVE',
    search: ''
  });

  // Fetch all licenses except ACTIVE and IN_GRACE_PERIOD
  const fetchLicenses = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const { page, limit } = pagination;
      const skip = (page - 1) * limit;
      
      // First, get the total count of all non-active, non-grace-period licenses
      const countResponse = await getLicenses(
        0,  // skip
        1,  // limit - we just need the total count
        undefined, // Don't filter by status on backend
        undefined, // type
        null,      // customerId
        null,      // expiresSoon
        filters.search // search
      );
      
      // Then fetch the actual data with pagination
      const response = await getLicenses(
        skip, 
        limit, 
        undefined, // Don't filter by status on backend
        undefined, // type
        null,      // customerId
        null,      // expiresSoon
        filters.search // search
      );
      
      if (response) {
        // Filter out ACTIVE and IN_GRACE_PERIOD statuses on the frontend
        const filteredItems = (response.items || []).filter(license => 
          license.status !== 'ACTIVE' && license.status !== 'IN_GRACE_PERIOD'
        );
        
        // Get the total count from the count response
        const total = countResponse?.total || 0;
        
        setLicenses(filteredItems);
        setPagination(prev => ({
          ...prev,
          total: total
        }));
      }
    } catch (error) {
      console.error('Error fetching inactive licenses:', error);
      message.error('Failed to load inactive licenses');
    } finally {
      setLoading(false);
    }
  }, [user?.id, JSON.stringify(pagination), filters.search]);

  // Export to Excel
  const exportToExcel = async () => {
    try {
      if (licenses.length === 0) {
        message.warning('No inactive licenses to export');
        return;
      }

      // Format data for Excel
      const excelData = await Promise.all(licenses.map(async (license) => {
        const customerInfo = license.customer_licenses?.[0];
        const customerId = customerInfo?.customer_id;

        let customerName = 'Unassigned';
        if (customerId) {
          try {
            const customer = await customerService.getCustomer(customerId);
            customerName = customer?.company_name || 'Unassigned';
          } catch (err) {
            console.warn(`Failed to fetch customer ${customerId}:`, err);
          }
        }
  
        return {
          'License Key': license.license_key,
          'Customer': customerName,
          'Type': license.license_type,
          'Status': license.status,
          'Max Companies': license.max_companies,
          'Max Users': license.max_users,
          'Activation Date': license.activation_date ? new Date(license.activation_date).toLocaleDateString() : 'N/A',
          'Expiry Date': license.expiry_date ? new Date(license.expiry_date).toLocaleDateString() : 'N/A',
          'Created At': license.created_at ? new Date(license.created_at).toLocaleString() : 'N/A',
        };
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Inactive Licenses');
      
      // Generate Excel file and trigger download
      XLSX.writeFile(workbook, `inactive_licenses_${new Date().toISOString().split('T')[0]}.xlsx`);
      message.success('Export to Excel successful');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      message.error('Failed to export to Excel');
    }
  };

  // Helper function to get customer name
  const getCustomerName = async (customerId) => {
    try {
      const customer = await customerService.getCustomer(customerId);
      return customer?.company_name || 'Unassigned';
    } catch (error) {
      console.error(`Error fetching customer ${customerId}:`, error);
      return 'Error loading customer';
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    try {
      if (licenses.length === 0) {
        message.warning('No inactive licenses to export');
        return;
      }

      // Initialize jsPDF
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Add title
      doc.setFontSize(18);
      doc.text('Inactive Licenses Report', 14, 20);
      
      // Add date
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);

      // Prepare table data
      const headers = [
        'License Key',
        'Customer',
        'Type',
        'Status',
        'Max Companies',
        'Max Users',
        'Expiry Date'
      ];

      const data = await Promise.all(licenses.map(async license => {
        const customerName = license.customer_licenses?.[0]?.customer_id 
          ? await getCustomerName(license.customer_licenses[0].customer_id)
          : 'Unassigned';
          
        return [
          license.license_key,
          customerName,
          license.license_type,
          license.status,
          license.max_companies,
          license.max_users,
          license.expiry_date ? new Date(license.expiry_date).toLocaleDateString() : 'N/A'
        ];
      }));

      // Add table
      autoTable(doc, {
        head: [headers],
        body: data,
        startY: 40,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          lineWidth: 0.1
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { top: 30 }
      });

      // Save the PDF
      doc.save(`inactive_licenses_${new Date().toISOString().split('T')[0]}.pdf`);
      message.success('Export to PDF successful');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      message.error('Failed to export to PDF');
    }
  };

  // Initial fetch when component mounts
  useEffect(() => {
    fetchLicenses();
    // We don't need fetchLicenses in the dependency array because we want to run this only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch when pagination or filters change
  useEffect(() => {
    fetchLicenses();
  }, [pagination.page, pagination.limit, filters.search, fetchLicenses]);

  const columns = [
    {
      title: 'License Key',
      dataIndex: 'license_key',
      key: 'license_key',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/admin/licenses/${record.id}`)}
          style={{ padding: 0, height: 'auto' }}
        >
          <code>{text}</code>
        </Button>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => <CustomerCell customerLicenses={record.customer_licenses} />
    },
    {
      title: 'Type',
      dataIndex: 'license_type',
      key: 'type',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <Tag color={
            status === 'ACTIVE' ? 'success' : 
            status === 'EXPIRED' ? 'warning' : 
            status === 'REVOKED' ? 'error' : 
            status === 'PENDING' ? 'default' : 
            status === 'SUSPENDED' ? 'error' : 
            status === 'INACTIVE' ? 'default' :
            status === 'IN_GRACE_PERIOD' ? 'warning' : 'default'
        }>
          {status}
        </Tag>
      )
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: date => date ? new Date(date).toLocaleDateString() : 'N/A'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button 
              type="link" 
              icon={<EditOutlined />}
              className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-30-px h-30-px d-flex justify-content-center align-items-center rounded-circle"
              onClick={() => navigate(`/admin/licenses/${record.id}/edit`)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div className="inactive-licenses-list">
      <Card
        title="Inactive Licenses Report"
        extra={
          <Space>
            <Tooltip title="Export to Excel">
              <Button 
                icon={<FileExcelOutlined />} 
                onClick={exportToExcel}
                disabled={licenses.length === 0}
              >
                Excel
              </Button>
            </Tooltip>
            <Tooltip title="Export to PDF">
              <Button 
                icon={<FilePdfOutlined />} 
                onClick={exportToPDF}
                disabled={licenses.length === 0}
                danger
              >
                PDF
              </Button>
            </Tooltip>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Search
            placeholder="Search inactive licenses..."
            allowClear
            enterButton={<SearchOutlined style={{ paddingTop: '8px' }}/>}
            onSearch={value => setFilters(prev => ({ ...prev, search: value }))}
            style={{ width: 300 }}
          />
        </div>
        
        <Table
          columns={columns}
          dataSource={licenses}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} inactive licenses`,
          }}
          onChange={(pagination) => setPagination(prev => ({
            ...prev,
            page: pagination.current,
            limit: pagination.pageSize
          }))}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default InactiveLicensesList;
